import { useEffect, useState } from "react";
import { Outlet, NavLink, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, 
  FolderOpen, 
  Plus,
  LogOut,
  Calculator,
  ChevronRight,
  Lightbulb,
  Map,
  Users,
  Calendar,
  UserCog,
  Hammer,
  FileBox,
  Settings,
  FileUp,
  Bell
} from "lucide-react";
import { cn } from "@/lib/utils";
import { logout, isRestrictedUser, isTelepro, isRegie, isAdmin, isAdministratif, getCurrentUser, getUserCommissions } from "@/pages/Login";
import { ThemeToggle } from "./ThemeToggle";

const navItemsFull = [
	{ path: "/", icon: LayoutDashboard, label: "Tableau de bord" },
	{ path: "/leads", icon: Users, label: "Lead / Télépro" },
	{ path: "/dossiers", icon: FolderOpen, label: "Dossiers" },
	{ path: "/simulateur-travaux", icon: Plus, label: "Nouveau dossier" },
	{ path: "/injection-pdf", icon: FileUp, label: "Injection PDF" },
	{ path: "/planning", icon: Calendar, label: "Planning" },
	{ path: "/simulateur-travaux", icon: Hammer, label: "Simulateur Intelligent" },
	{ path: "/simulateur", icon: Calculator, label: "Simulateur 143 / 171" },
	{ path: "/simulateur-174", icon: Calculator, label: "Simulateur 174" },
	{ path: "/carte", icon: Map, label: "Carte" },
	{ path: "/parametres", icon: Settings, label: "Paramètres" },
];

const navItemsRestricted = [
	{ path: "/", icon: LayoutDashboard, label: "Tableau de bord" },
	{ path: "/leads", icon: Users, label: "Leads" },
	{ path: "/dossiers", icon: FolderOpen, label: "Dossiers" },
	{ path: "/simulateur-travaux", icon: Plus, label: "Nouveau dossier" },
	{ path: "/injection-pdf", icon: FileUp, label: "Injection PDF" },
	{ path: "/planning", icon: Calendar, label: "Planning" },
	{ path: "/simulateur-travaux", icon: Hammer, label: "Simulateur Intelligent" },
	{ path: "/carte", icon: Map, label: "Carte" },
	{ path: "/parametres", icon: Settings, label: "Paramètres" },
];

const navItemsAdministratif = [
	{ path: "/", icon: LayoutDashboard, label: "Tableau de bord" },
	{ path: "/leads", icon: Users, label: "Leads" },
	{ path: "/dossiers", icon: FolderOpen, label: "Dossiers" },
	{ path: "/simulateur-travaux", icon: Plus, label: "Nouveau dossier" },
	{ path: "/injection-pdf", icon: FileUp, label: "Injection PDF" },
	{ path: "/planning", icon: Calendar, label: "Planning" },
	{ path: "/simulateur-travaux", icon: Hammer, label: "Simulateur Intelligent" },
	{ path: "/carte", icon: Map, label: "Carte" },
	{ path: "/parametres", icon: Settings, label: "Paramètres" },
];

const navItemsTelepro = [
	{ path: "/dashboard-regie", icon: LayoutDashboard, label: "Tableau de bord" },
	{ path: "/leads", icon: Users, label: "Leads" },
	{ path: "/dossiers-regie", icon: FileBox, label: "Dossiers" },
	{ path: "/simulateur-travaux", icon: Hammer, label: "Simulateur / Nouveau dossier" },
	{ path: "/injection-pdf", icon: FileUp, label: "Injection PDF" },
	{ path: "/etudes-regie", icon: FolderOpen, label: "Études" },
	{ path: "/planning-regie", icon: Calendar, label: "Planning" },
	{ path: "/carte", icon: Map, label: "Carte" },
];

const navItemsTeleproDynamic = [
	{ path: "/dashboard-regie", icon: LayoutDashboard, label: "Tableau de bord" },
	{ path: "/leads", icon: Users, label: "Leads" },
	{ path: "/dossiers-regie", icon: FileBox, label: "Dossiers" },
	{ path: "/simulateur-travaux", icon: Hammer, label: "Simulateur / Nouveau dossier" },
	{ path: "/injection-pdf", icon: FileUp, label: "Injection PDF" },
	{ path: "/etudes-regie", icon: FolderOpen, label: "Études" },
	{ path: "/planning-regie", icon: Calendar, label: "Planning" },
	{ path: "/carte", icon: Map, label: "Carte" },
];

const navItemsRegie = [
	{ path: "/dashboard-regie", icon: LayoutDashboard, label: "Tableau de bord" },
	{ path: "/dossiers-regie", icon: FileBox, label: "Dossiers" },
	{ path: "/simulateur-travaux", icon: Hammer, label: "Simulateur / Nouveau dossier" },
	{ path: "/injection-pdf", icon: FileUp, label: "Injection PDF" },
	{ path: "/etudes-regie", icon: FolderOpen, label: "Études" },
	{ path: "/planning-regie", icon: Calendar, label: "Planning" },
	{ path: "/carte", icon: Map, label: "Carte" },
];

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const telepro = isTelepro();
  const isRestricted = isRestrictedUser();
  const regie = isRegie();
  const administratif = isAdministratif();
  const hasDynamicCommissions = !!getUserCommissions();
  
  let navItems;
  if (telepro && hasDynamicCommissions) {
    navItems = navItemsTeleproDynamic;
  } else if (telepro) {
    navItems = navItemsTelepro;
  } else if (regie) {
    navItems = navItemsRegie;
  } else if (administratif) {
    navItems = navItemsAdministratif;
  } else if (isRestricted) {
    navItems = navItemsRestricted;
  } else {
    navItems = navItemsFull;
  }

  const isRegieOrDynamicTelepro = regie || telepro;
  const allowedPaths = isRegieOrDynamicTelepro
    ? ["/dashboard-regie", "/simulateur-travaux", "/etudes-regie", "/dossiers-regie", "/planning-regie", "/leads", "/carte"]
    : [];
  
  useEffect(() => {
    if (isRegieOrDynamicTelepro) {
      const path = location.pathname;
      const isAllowed = allowedPaths.some(p => path === p || path.startsWith("/etude/") || path.startsWith("/dossier-regie/") || path.startsWith("/leads/") || path.startsWith("/simulateur") || path === "/injection-pdf");
      if (!isAllowed) {
        navigate("/dashboard-regie", { replace: true });
      }
    }
  }, [isRegieOrDynamicTelepro, location.pathname, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Notification bell component with dropdown panel
  const NotifBell = () => {
    const [count, setCount] = useState(0);
    const [open, setOpen] = useState(false);
    const [notifs, setNotifs] = useState([]);
    const usr = localStorage.getItem("label_energie_user") || "";
    const rl = localStorage.getItem("label_energie_role") || "";
    const apiBase = `${typeof window !== "undefined" ? window.location.origin : ""}/api`;

    useEffect(() => {
      const fetchCount = async () => {
        try {
          const res = await fetch(`${apiBase}/notifications?limit=1&user=${encodeURIComponent(usr)}&role=${encodeURIComponent(rl)}`);
          const data = await res.json();
          setCount(data.unread_count || 0);
        } catch (err) {}
      };
      fetchCount();
      const interval = setInterval(fetchCount, 15000);
      return () => clearInterval(interval);
    }, [usr, rl, apiBase]);

    const loadNotifs = async () => {
      try {
        const res = await fetch(`${apiBase}/notifications?limit=15&user=${encodeURIComponent(usr)}&role=${encodeURIComponent(rl)}`);
        const data = await res.json();
        setNotifs(data.notifications || []);
        setCount(data.unread_count || 0);
      } catch (err) {}
    };

    const markAllRead = async () => {
      try {
        await fetch(`${apiBase}/notifications/read-all?user=${encodeURIComponent(usr)}&role=${encodeURIComponent(rl)}`, { method: "PUT" });
        setCount(0);
        setNotifs(prev => prev.map(n => ({ ...n, read: true })));
      } catch (err) {}
    };

    const handleToggle = () => {
      if (!open) loadNotifs();
      setOpen(!open);
    };

    const isRegTel = rl === "regie" || rl === "telepro";

    return (
      <div className="relative">
        <button onClick={handleToggle} className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg text-[13px] font-medium w-full text-white/60 hover:bg-white/10 transition-all relative">
          <Bell className="w-4 h-4" />
          <span>Notifications</span>
          {count > 0 && <span className="absolute top-0 left-6 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{count}</span>}
        </button>
        {open && (
          <div className="absolute bottom-full left-0 mb-2 w-80 max-h-[400px] bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-[100]">
            <div className="p-3 border-b border-border flex items-center justify-between bg-muted/50">
              <span className="font-semibold text-sm">Notifications</span>
              <div className="flex gap-1">
                {count > 0 && <button onClick={markAllRead} className="text-[10px] text-emerald-600 font-bold hover:underline">Tout lire</button>}
                <button onClick={() => setOpen(false)} className="text-xs text-muted-foreground ml-2">✕</button>
              </div>
            </div>
            <div className="overflow-y-auto max-h-[340px] divide-y divide-border">
              {notifs.length === 0 ? (
                <div className="p-6 text-center text-muted-foreground text-xs">Aucune notification</div>
              ) : notifs.map(n => (
                <div key={n.id} className={cn("p-3 text-xs hover:bg-muted/30 cursor-pointer transition-colors", !n.read && "bg-emerald-500/10")}
                  onClick={() => { if (n.dossier_id) { setOpen(false); navigate(isRegTel ? `/dossier-regie/${n.dossier_id}` : `/dossiers/${n.dossier_id}`); } }}>
                  <p className="font-semibold">{n.message}</p>
                  <p className="text-[10px] text-muted-foreground mt-0.5">
                    {n.created_at ? new Date(n.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}
                    {n.created_by && ` — ${n.created_by}`}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Sidebar - Dark Slate Blue */}
      <aside className="w-64 bg-[hsl(var(--sidebar-bg))] fixed h-screen flex flex-col shadow-2xl z-50">
        {/* Logo Section */}
        <div className="px-4 py-4 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[hsl(var(--renova-green))] flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-heading font-bold text-lg text-white tracking-tight">
                RENOVA
              </h1>
              <p className="text-xs text-[hsl(var(--renova-cyan))] font-medium tracking-widest">
                SOLUTION
              </p>
            </div>
          </div>
        </div>

        {/* Navigation — compact to fit all items without scroll */}
        <nav className="flex-1 px-3 py-2 space-y-0.5 overflow-hidden">
          {navItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <NavLink
                key={item.label}
                to={item.path}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-200",
                  isActive
                    ? "bg-[hsl(var(--renova-green))]/20 text-white shadow-lg"
                    : "text-white/60 hover:text-white hover:bg-white/10"
                )}
              >
                <item.icon className={cn(
                  "w-4 h-4 shrink-0 transition-colors",
                  isActive ? "text-[hsl(var(--renova-green))]" : ""
                )} />
                <span className="flex-1 truncate">{item.label}</span>
                {isActive && (
                  <ChevronRight className="w-3.5 h-3.5 text-[hsl(var(--renova-green))]" />
                )}
              </NavLink>
            );
          })}
        </nav>

        {/* Footer — compact */}
        <div className="px-3 py-2 border-t border-white/10 space-y-1.5">
          {/* Notification Bell */}
          <NotifBell />
          {/* Theme + Logout row */}
          <div className="flex items-center justify-between px-1">
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium text-white/50 hover:bg-red-500/20 hover:text-red-400 transition-all"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Déconnexion</span>
            </button>
            <ThemeToggle />
          </div>
          <p className="text-[10px] text-white/30 text-center">
            Cryptodigger © 2026
          </p>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 min-h-screen p-6">
        <Outlet />
      </main>
    </div>
  );
}
